import React from "react";
import ReactDOM from "react-dom";
import { Combobox } from "./react";

// Indentation prefix for child items (4 non-breaking spaces)
const CHILD_PREFIX = "\u00A0\u00A0\u00A0\u00A0";

/**
 * Flatten nested options tree for downshift (which requires flat items array)
 * Also builds labels lookup for displaying selected items
 *
 * Input format: [{ label, value, children? }, ...]
 * Output format: [{ label, value, displayLabel }, ...] (flat)
 *
 * @param {Array} tree - Nested options from server
 * @param {number} level - Current nesting level (for indentation)
 * @param {Object} labels - Accumulator for { value: label } lookup
 * @returns {Array} Flat array of options for downshift
 */
function flattenOptions(tree, level = 0, labels = {}) {
  const result = [];
  const indent = CHILD_PREFIX.repeat(level);

  for (const item of tree) {
    const { label, value, children } = item;

    if (value) {
      labels[String(value)] = label;
      result.push({
        label,
        value,
        displayLabel: indent + label,
      });
    }

    if (children) {
      result.push(...flattenOptions(children, level + 1, labels));
    }
  }

  return result;
}

function mountCombobox(wrapperElement) {
  try {
    const { dataset, id: selectId } = wrapperElement;

    const {
      labelId,
      ariaLabel,
      name,
      contextName,
      emptyLabel,
      placeholder,
      options: rawOptions = "[]",
      selected: rawSelected = "[]",
    } = dataset;

    if (!name) {
      throw new Error("Combobox requires data-name attribute");
    }

    // Parse nested options and flatten for downshift
    const optionsTree = JSON.parse(rawOptions);
    const initialSelected = JSON.parse(rawSelected);

    const labels = {};
    const options = flattenOptions(optionsTree, 0, labels);

    // Track selected items in state (normalize to strings for consistent comparison)
    let currentSelected = initialSelected.map(String);

    // Sync the filter-row's empty-value hidden input used for required-field
    // validation on the PESF. The input already exists in the Liquid template;
    // we keep its value in sync with the selection state so FormValidator's
    // "required" check works without a separate sentinel input.
    const filterRow = wrapperElement.closest("[id^='filter-row-']");
    const validationInput = filterRow?.querySelector(
      "input[type='hidden'][data-validation]",
    );
    const syncValidation = () => {
      if (validationInput) {
        validationInput.value = currentSelected.length > 0 ? "selected" : "";
        validationInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    };

    const dispatchFilterEvent = (eventName, tagId) => {
      const wrapper = document.getElementById(selectId);
      if (wrapper) {
        wrapper.dispatchEvent(
          new CustomEvent(eventName, {
            detail: { contextName, tag_id: tagId },
          }),
        );
      }
    };

    const onTagAdded = (id) => {
      if (!currentSelected.includes(id)) {
        currentSelected = [...currentSelected, id];
        dispatchFilterEvent("slzr:filter:add", id);
        syncValidation();
      }
    };

    const onTagRemoved = (id) => {
      currentSelected = currentSelected.filter((tagId) => tagId !== id);
      dispatchFilterEvent("slzr:filter:remove", id);
      syncValidation();
    };

    ReactDOM.render(
      React.createElement(Combobox, {
        id: selectId,
        labelId,
        ariaLabel,
        options,
        selected: currentSelected,
        onTagAdded,
        onTagRemoved,
        labels,
        name,
        emptyLabel,
        placeholder,
      }),
      wrapperElement,
    );
  } catch (error) {
    // Silent fail in production - errors logged to console only in development
    if (process.env.NODE_ENV !== "production") {
      console.error("Error mounting Combobox:", error);
    }
  }
}

export function initializeComboboxes() {
  document.querySelectorAll("[data-filter-combobox]").forEach(mountCombobox);
}
